// Route configuration for the application
export const ROUTES = {
  PUBLIC: {
    HOME: "/",
    HOME_ALT: "/home",
    INTERNSHIPS: "/internships",
    JOBS: "/jobs",
    MENTORSHIPS: "/mentorships",
    LOGIN: "/login",
    REGISTER: "/register",
    CHOOSE_PLAN: "/choose-plan",

    EVENTS: "/events",
    NEWS_MEDIA: "/news-media",
    NEWS_MEDIA_DETAIL: "/news-media/:id",
    SCHOLARSHIPS: "/scholarships",
    PROGRAMS: "/programs",
    COMMUNITY: "/community",
    REMOTE_HUB: "/remote-hub",
    LEARNING_HUB: "/learning-hub",
    SURROGATE_LOGIN: "/surrogate-login",

    /** Public program landing — use `programPublicPath(code)` for concrete URLs */
    PROGRAM_DETAIL: "/programs/:programCode",
    /** Shared payment return handler (iframe) for all checkout flows */
    PAYMENT_RETURN: "/payment/return",
    /** Legacy alias — same handler as PAYMENT_RETURN */
    PROGRAM_PAYMENT_RETURN: "/programs/payment-return",
    /** Legacy slug; redirects to IgniteX 2026 UUID in App.tsx */
    PROGRAM_IGNITEX_2026_LEGACY: "/programs/ignite-x-2026",

    PROFILE: "/profile",
  },
  STUDENT: {
    DASHBOARD: "/student/dashboard",
    PROFILE: "/student/profile",
    INTERNSHIPS: "/student/internships",
    INTERNSHIP_DETAILS: "/student/internships/:internshipCode",
    INTERNSHIP_APPLY: "/student/internships/:internshipCode/apply",
    APPLICATIONS: "/student/applications",
    APPLICATION_DETAILS: "/student/applications/:id",
    JOB_APPLICATION_DETAILS: "/student/job-applications/:applicationId",
    MENTORSHIP_APPLICATIONS: "/student/mentorship-applications",
    MENTORSHIP_APPLICATION_DETAILS: "/student/mentorship-applications/:bookingId",
    CONVERSATIONS: "/student/conversations",
    EDUCATION: "/profile/education",
    EDUCATION_ADD: "/profile/education/add",
    EDUCATION_EDIT: "/profile/education/:id/edit",
    WORK_EXPERIENCE: "/profile/work-experience",
    /** Student view of an enrolled program (by enrollment code) */
    PROGRAM_ENROLLMENT_VIEW: "/student/programs/enrollment/:enrollmentCode",
  },
  ORGANIZATION: {
    DASHBOARD: "/organization/dashboard",
    INTERNSHIPS: "/organization/internships",
    APPLICATIONS: "/organization/applications",
    APPLICATION_REVIEW: "/organization/applications/:id/review",
    OFFER_INTERNSHIP: "/organization/offer-internship",
    PROFILE: "/organization/profile",
    CREATE_INTERNSHIP: "/organization/internships/create",
    INTERNSHIP_VIEW: "/organization/internships/:internshipCode",
    EDIT_INTERNSHIP: "/organization/internships/:internshipCode/edit",
    CREATE_JOB: "/organization/jobs/create",
    JOB_VIEW: "/organization/jobs/:jobId",
    EDIT_JOB: "/organization/jobs/:jobId/edit",
    JOB_APPLICATION_REVIEW: "/organization/job-applications/:applicationId/review",
    MENTORSHIP_APPLICATIONS: "/organization/mentorship-applications",
    MENTORSHIP_APPLICATION_DETAILS: "/organization/mentorship-applications/:bookingId",
    PROFESSIONAL_SERVICES_ENQUIRY_DETAILS: "/organization/professional-services-enquiries/:id",
    SUBSCRIPTION: "/organization/subscription",
  },
  MENTOR: {
    DASHBOARD: "/mentor/dashboard",
    PROFILE: "/mentor/profile",
    EDUCATION: "/mentor/education",
    EDUCATION_ADD: "/mentor/education/add",
    EDUCATION_EDIT: "/mentor/education/:id/edit",
    EXPERIENCE: "/mentor/experience",
    PREFERENCE: "/mentor/preference",
    MENTORSHIP_APPLICATION_DETAILS: "/mentorship/applications/:applicationId",
  },
  SPONSOR: {
    DASHBOARD: "/sponsor/dashboard",
    PROFILE: "/sponsor/profile",
    STUDENT_DETAILS: "/sponsor/student/:id",
    SCHOLARSHIP_DETAILS: "/sponsor/scholarship/:id",
  },
  INTERNSHIP: {
    DETAILS: "/internships/:internshipCode",
    EDIT: "/internships/:internshipCode/edit",
  },
  UTILITY: {
    TEST_API: "/test-api",
  },
  INBOX: "/inbox",
} as const;

/** IgniteX 2026 (and other links that should open this program by default) */
export const DEFAULT_PUBLIC_PROGRAM_CODE =
  "a04d6aed-b861-401a-9076-e7bf2cc58270" as const;
 //"07adbef3-e6e2-4f7d-a07e-f1addb7d01c1" as const;

export function programPublicPath(programCode: string): string {
  return `/programs/${programCode}`;
}

export function programEnrollmentViewPath(enrollmentCode: string): string {
  return `/student/programs/enrollment/${encodeURIComponent(enrollmentCode)}`;
}

// Route metadata for navigation and breadcrumbs
export const ROUTE_METADATA = {
  [ROUTES.PUBLIC.HOME]: { title: "Home", requiresAuth: false },
  [ROUTES.PUBLIC.HOME_ALT]: { title: "Home", requiresAuth: false },
  [ROUTES.PUBLIC.INTERNSHIPS]: { title: "Internships", requiresAuth: false },
  [ROUTES.PUBLIC.JOBS]: { title: "Jobs", requiresAuth: false },
  [ROUTES.PUBLIC.MENTORSHIPS]: { title: "Mentorships", requiresAuth: false },
  [ROUTES.PUBLIC.LOGIN]: { title: "Login", requiresAuth: false },
  [ROUTES.PUBLIC.REGISTER]: { title: "Register", requiresAuth: false },
  [ROUTES.PUBLIC.CHOOSE_PLAN]: { title: "Choose Plan", requiresAuth: false },

  [ROUTES.PUBLIC.EVENTS]: { title: "Events", requiresAuth: false },
  [ROUTES.PUBLIC.NEWS_MEDIA]: { title: "News and Media", requiresAuth: false },
  [ROUTES.PUBLIC.NEWS_MEDIA_DETAIL]: { title: "News and Media Detail", requiresAuth: false },
  [ROUTES.PUBLIC.SCHOLARSHIPS]: { title: "Scholarships", requiresAuth: false },
  [ROUTES.PUBLIC.COMMUNITY]: { title: "Community", requiresAuth: false },
  [programPublicPath(DEFAULT_PUBLIC_PROGRAM_CODE)]: {
    title: "Program",
    requiresAuth: false,
  },
  [ROUTES.PUBLIC.PROFILE]: { title: "Profile", requiresAuth: true },
  [ROUTES.STUDENT.DASHBOARD]: {
    title: "Student Dashboard",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.PROFILE]: { title: "Student Profile", requiresAuth: true },
  [ROUTES.STUDENT.INTERNSHIPS]: { title: "My Internships", requiresAuth: true },
  [ROUTES.STUDENT.INTERNSHIP_DETAILS]: {
    title: "Internship Details",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.INTERNSHIP_APPLY]: {
    title: "Apply for Internship",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.APPLICATIONS]: {
    title: "My Applications",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.APPLICATION_DETAILS]: {
    title: "Application Details",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.JOB_APPLICATION_DETAILS]: {
    title: "Job Application Details",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.CONVERSATIONS]: {
    title: "All Conversations",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.PROGRAM_ENROLLMENT_VIEW]: {
    title: "My Program Enrollment",
    requiresAuth: true,
  },
  [ROUTES.STUDENT.EDUCATION]: { title: "Education", requiresAuth: true },
  [ROUTES.STUDENT.EDUCATION_ADD]: { title: "Add Course", requiresAuth: true },
  [ROUTES.STUDENT.EDUCATION_EDIT]: { title: "Edit Course", requiresAuth: true },
  [ROUTES.ORGANIZATION.DASHBOARD]: {
    title: "Organization Dashboard",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.INTERNSHIPS]: {
    title: "Organization Internships",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.APPLICATIONS]: {
    title: "Organization Applications",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.APPLICATION_REVIEW]: {
    title: "Application Review",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.JOB_APPLICATION_REVIEW]: {
    title: "Job Application Review",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.OFFER_INTERNSHIP]: {
    title: "Offer Internship",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.INTERNSHIP_VIEW]: {
    title: "View Internship",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.EDIT_INTERNSHIP]: {
    title: "Edit Internship",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.CREATE_JOB]: {
    title: "Create Job",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.JOB_VIEW]: {
    title: "Job Details",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.EDIT_JOB]: {
    title: "Edit Job",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.MENTORSHIP_APPLICATIONS]: {
    title: "Mentorship Applications",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.MENTORSHIP_APPLICATION_DETAILS]: {
    title: "Mentorship Application Details",
    requiresAuth: true,
  },
  [ROUTES.ORGANIZATION.PROFESSIONAL_SERVICES_ENQUIRY_DETAILS]: {
    title: "Professional Services Enquiry Details",
    requiresAuth: true,
  },
  [ROUTES.INTERNSHIP.DETAILS]: {
    title: "View Internship",
    requiresAuth: false,
  },
  [ROUTES.INTERNSHIP.EDIT]: { title: "Edit Internship", requiresAuth: true },
  [ROUTES.SPONSOR.DASHBOARD]: {
    title: "Sponsor Dashboard",
    requiresAuth: true,
  },
  [ROUTES.SPONSOR.PROFILE]: { title: "Sponsor Profile", requiresAuth: true },
  [ROUTES.UTILITY.TEST_API]: { title: "Test API", requiresAuth: false },
} as const;

// Helper function to get route metadata
export const getRouteMetadata = (path: string) => {
  return (
    ROUTE_METADATA[path as keyof typeof ROUTE_METADATA] || {
      title: "Page Not Found",
      requiresAuth: false,
    }
  );
};

// Helper function to check if route requires authentication
export const isRouteProtected = (path: string): boolean => {
  const metadata = getRouteMetadata(path);
  return metadata.requiresAuth;
};
